import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Breadcrumbs from "../components/Breadcrumbs";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import corpBg from "../assets/images/corporate/corporate-main-bg-img.jpg";
import axios from "axios";
import { lawyerAuth } from "../services/lawyerApi";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";

const toOptions = (arr = []) =>
  arr.map((item) => ({ value: item.id, label: item.name }));

const selectStyles = {
  control: (base, state) => ({
    ...base,
    background: "#fff",
    border: state.isFocused ? "1px solid #000" : "1px solid #e1e1e1",
    borderRadius: "12px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0,0,0,0.08)" : "none",
    minHeight: "52px",
    fontSize: "15px",
    cursor: "pointer",
    "&:hover": { borderColor: "#aaa" },
  }),
  placeholder: (base) => ({ ...base, color: "#a6a6a6", fontSize: "15px" }),
  multiValue: (base) => ({
    ...base,
    background: "#f0f0f0",
    borderRadius: "8px",
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#111", fontWeight: 500 }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: "0 8px 8px 0",
    "&:hover": { background: "#ddd", color: "#000" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
    border: "1px solid #e1e1e1",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? "#111" : state.isFocused ? "#f5f6f8" : "#fff",
    color: state.isSelected ? "#fff" : "#333",
    fontSize: "14px",
    cursor: "pointer",
  }),
};

const LawyerRegistration = () => {
  const navigate = useNavigate();
  const [showTop, setShowTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Form state
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("ae");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [about, setAbout] = useState("");
  const [picture, setPicture] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [license, setLicense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [arbitrators, setArbitrators] = useState([]);

  // Dropdown options from API
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [arbitratorOptions, setArbitratorOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const pictureRef = useRef(null);
  const certificateRef = useRef(null);
  const licenseRef = useRef(null);

  // Fetch dropdown data from API
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/getDropdownData`);
        if (res?.data?.status) {
          const d = res.data.data;
          setLanguageOptions((d.languages || []).map((item) => ({ value: item.code, label: item.name })));
          setJurisdictionOptions(toOptions(d.jurisdictions));
          setArbitratorOptions(toOptions(d.arbitrators));
          setCountryOptions(toOptions(d.countries));
        }
      } catch (e) {
        console.error("Error fetching dropdown data:", e);
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/getCategories`);
        if (res?.data?.status) {
          const data = res.data.data;
          const list = Array.isArray(data) ? data : data.categories || [];
          setCategoryOptions(toOptions(list));
        }
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    };

    fetchDropdownData();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!categories.length) {
      setSubCategoryOptions([]);
      setSubCategories([]);
      return;
    }
    const fetchSubCategories = async () => {
      try {
        const ids = categories.map((c) => c.value);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getSubCategories`,
          { params: { categories: JSON.stringify(ids) } }
        );
        if (res?.data?.status) {
          const data = res.data.data;
          const list = Array.isArray(data) ? data : data.sub_categories || [];
          setSubCategoryOptions(toOptions(list));
          // drop any previously selected sub-cats that no longer exist
          setSubCategories((prev) => {
            const valid = new Set(list.map((i) => i.id));
            return prev.filter((s) => valid.has(s.value));
          });
        }
      } catch (e) {
        console.error("Error fetching sub-categories:", e);
      }
    };
    fetchSubCategories();
  }, [categories]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => {
      setShowTop(window.pageYOffset > 400);
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      setScrollProgress(max > 0 ? doc.scrollTop / max : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = " name is required.";
    if (!email.trim()) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.email = "Please enter a valid email.";
    }
    if (!phone.trim()) {
      e.phone = "Phone number is required.";
    } else if (phone.replace(/\D/g, "").length < 7) {
      e.phone = "Please enter a valid phone number.";
    }
    if (!city.trim()) e.city = "City is required.";
    if (!country) e.country = "Country is required.";
    if (!languages.length) e.languages = "Select at least one language.";
    if (!about.trim()) e.about = "Please write a short bio.";
    if (!picture) e.picture = "Profile picture is required.";
    if (!certificate) e.certificate = "Certificate is required.";
    if (!license) e.license = "License is required.";
    if (!categories.length) e.categories = "Select at least one category.";
    if (!jurisdictions.length) e.jurisdictions = "Select at least one jurisdiction.";
    return e;
  };

  const resetForm = () => {
    setName(""); setEmail(""); setPhone("");
    setCountryCode("ae"); setCity(""); setCountry(null);
    setLanguages([]); setAbout(""); setPicture(null);
    setCertificate(null); setLicense(null);
    setCategories([]); setSubCategories([]); setJurisdictions([]); setArbitrators([]);
    if (pictureRef.current) pictureRef.current.value = "";
    if (certificateRef.current) certificateRef.current.value = "";
    if (licenseRef.current) licenseRef.current.value = "";
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields correctly.");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const form = new FormData();
      console.log(country);
      form.append("source", "web");
      form.append("type", "individual");
      form.append("name", name);
      form.append("email", email);
      form.append("country_code", countryCode.toUpperCase());
      form.append("phone", phone.replace(/\D/g, ""));
      form.append("city", city);
      form.append("country", country.label);
      form.append("nationality", country.label);
      form.append("languages", JSON.stringify(languages.map((l) => l.label)));
      form.append("about", about);
      form.append("picture", picture);
      form.append("certificate", certificate);
      form.append("license", license);
      form.append("categories", JSON.stringify(categories.map((c) => c.value)));
      form.append("sub_categories", JSON.stringify(subCategories.map((s) => s.value)));
      form.append("jurisdictions", JSON.stringify(jurisdictions.map((j) => j.value)));
      form.append("arbitrators", JSON.stringify(arbitrators.map((a) => a.value)));

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/lawyer/register`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res?.data?.status) {
        resetForm();
        const { auth_token: token, lawyer } = res.data.data || {};
        if (token) {
          // Sign them in; the portal shows "under approval" until admin approves.
          lawyerAuth.setSession(token, { ...(lawyer || {}), is_approved: 0 });
          navigate("/lawyer/dashboard", { replace: true });
        } else {
          navigate("/thank-you");
        }
      } else {
        toast.error(res?.data?.message || "Submission failed.");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Unable to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-inter">
      <Seo
        title="Lawyer Registration — Legal Platform"
        description="Join Legal Platform as a verified lawyer and connect with clients across jurisdictions."
        canonicalPath="/lawyer-registration"
        image="/favicon.png"
      />
      <Header />

      {/* Hero — compact banner only */}
      <div className="corp-hero lreg-hero">
        <div className="container h-100">
          <div className="row h-100 align-items-center">
            <div
              className="col-lg-8 d-flex flex-column justify-content-center align-items-start text-start"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="corp-hero-textwrap">
                <h1 className="corp-hero-title">Join as a Verified Lawyer</h1>
                <p className="corp-hero-sub">
                  Connect with clients globally. Build your practice on a trusted legal platform.
                </p>
              </div>
            </div>
          </div>
        </div>
        <img src={corpBg} alt="" className="corp-hero-bg" />
      </div>

      <Breadcrumbs />

      {/* Form + sidebar below hero — normal document flow */}
      <main className="container lreg-main">
        <div className="row g-4 align-items-start">

          {/* ── Form column ── */}
          <div className="col-lg-7" data-aos="fade-up" >
            <div className="corp-form-card lreg-form-card">
              <h5 className="lreg-form-title">Lawyer Registration</h5>

              {/* Name */}
              <div className="row g-2">
                <div className="col-12">
                  <label className="corp-label">Full Name <span className="corp-asterisk">*</span></label>
                  <input
                    type="text"
                    className={`form-control corp-input ${errors.name ? "is-invalid" : ""}`}
                    placeholder="First Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
               
              </div>

              {/* Email & Phone */}
              <div className="row g-2 mt-1">
                <div className="col-6">
                  <label className="corp-label">Email <span className="corp-asterisk">*</span></label>
                  <input
                    type="email"
                    className={`form-control corp-input ${errors.email ? "is-invalid" : ""}`}
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                <div className="col-6">
                  <label className="corp-label">Phone <span className="corp-asterisk">*</span></label>
                  <div className="corp-phone-input-wrapper">
                    <PhoneInput
                      country={"ae"}
                      value={phone}
                      onChange={(value, data) => {
                        setPhone(value);
                        setCountryCode(data.countryCode);
                      }}
                      containerClass="corp-phone-container"
                      inputClass={`form-control corp-input corp-phone-input-field ${errors.phone ? "is-invalid" : ""}`}
                      buttonClass="corp-phone-button"
                      dropdownClass="corp-phone-dropdown"
                      placeholder="50 123 4567"
                      enableSearch={true}
                      searchPlaceholder="Search country..."
                      searchClass="corp-phone-search"
                      limitMaxLength={true}
                      inputProps={{ name: "phone", required: true }}
                    />
                  </div>
                  {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
                </div>
              </div>

              {/* City & Country */}
              <div className="row g-2 mt-1">
                <div className="col-6">
                  <label className="corp-label">City <span className="corp-asterisk">*</span></label>
                  <input
                    type="text"
                    className={`form-control corp-input ${errors.city ? "is-invalid" : ""}`}
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                  {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                </div>
                <div className="col-6">
                  <label className="corp-label">Country <span className="corp-asterisk">*</span></label>
                  <Select
                    options={countryOptions}
                    value={country}
                    label="name"
                    valueField="name"
                    onChange={setCountry}
                    placeholder="Select Country"
                    styles={selectStyles}
                    classNamePrefix="lreg-select"
                  />
                  {errors.country && <div className="lreg-field-error">{errors.country}</div>}
                </div>
              </div>

              {/* Languages */}
              <div className="mt-2">
                <label className="corp-label">Languages <span className="corp-asterisk">*</span></label>
              
                <Select
                  isMulti
                  options={languageOptions}
                  label="name"
                  valueField="name"
                  value={languages}
                  onChange={(val) => setLanguages(val || [])}
                  placeholder="Select Languages..."
                  styles={selectStyles}
                  classNamePrefix="lreg-select"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                />
                {errors.languages && <div className="lreg-field-error">{errors.languages}</div>}
              </div>

              {/* About */}
              <div className="mt-2">
                <label className="corp-label">About / Bio <span className="corp-asterisk">*</span></label>
                <textarea
                  rows={3}
                  className={`form-control corp-input lreg-textarea ${errors.about ? "is-invalid" : ""}`}
                  placeholder="Write a short professional bio..."
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                />
                {errors.about && <div className="invalid-feedback">{errors.about}</div>}
              </div>

              {/* Files: Picture & Certificate */}
              <div className="row g-2 mt-1">
                <div className="col-6">
                  <label className="corp-label">Profile Picture <span className="corp-asterisk">*</span></label>
                  <input
                    type="file"
                    accept="image/*"
                    className={`form-control corp-input ${errors.picture ? "is-invalid" : ""}`}
                    onChange={(e) => setPicture(e.target.files?.[0] || null)}
                    ref={pictureRef}
                  />
                  {errors.picture && <div className="invalid-feedback">{errors.picture}</div>}
                </div>
                <div className="col-6">
                  <label className="corp-label">Certificate <span className="corp-asterisk">*</span></label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className={`form-control corp-input ${errors.certificate ? "is-invalid" : ""}`}
                    onChange={(e) => setCertificate(e.target.files?.[0] || null)}
                    ref={certificateRef}
                  />
                  {errors.certificate && <div className="invalid-feedback">{errors.certificate}</div>}
                </div>
              </div>

              {/* License */}
              <div className="mt-2">
                <label className="corp-label">License <span className="corp-asterisk">*</span></label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className={`form-control corp-input ${errors.license ? "is-invalid" : ""}`}
                  onChange={(e) => setLicense(e.target.files?.[0] || null)}
                  ref={licenseRef}
                />
                {errors.license && <div className="invalid-feedback">{errors.license}</div>}
              </div>

              {/* Categories + Sub-Categories */}
              <div className="row g-2 mt-1">
                <div className="col-6">
                  <label className="corp-label">Lawyer Categories <span className="corp-asterisk">*</span></label>
                  <Select
                    isMulti
                    options={categoryOptions}
                    value={categories}
                    onChange={(val) => setCategories(val || [])}
                    placeholder="Select Categories..."
                    styles={selectStyles}
                    classNamePrefix="lreg-select"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                  {errors.categories && <div className="lreg-field-error">{errors.categories}</div>}
                </div>
                <div className="col-6">
                  <label className="corp-label">Sub-Categories <span className="lreg-optional">(optional)</span></label>
                  <Select
                    isMulti
                    options={subCategoryOptions}
                    value={subCategories}
                    onChange={(val) => setSubCategories(val || [])}
                    placeholder="Select Sub-Categories..."
                    styles={selectStyles}
                    classNamePrefix="lreg-select"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                </div>
              </div>

              {/* Jurisdictions + Arbitrators */}
              <div className="row g-2 mt-1">
                <div className="col-6">
                  <label className="corp-label">Lawyer Jurisdictions <span className="corp-asterisk">*</span></label>
                  <Select
                    isMulti
                    options={jurisdictionOptions}
                    value={jurisdictions}
                    onChange={(val) => setJurisdictions(val || [])}
                    placeholder="Select Jurisdictions..."
                    styles={selectStyles}
                    classNamePrefix="lreg-select"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                  {errors.jurisdictions && <div className="lreg-field-error">{errors.jurisdictions}</div>}
                </div>
                <div className="col-6">
                  <label className="corp-label">Arbitrators <span className="lreg-optional">(optional)</span></label>
                  <Select
                    isMulti
                    options={arbitratorOptions}
                    value={arbitrators}
                    onChange={(val) => setArbitrators(val || [])}
                    placeholder="Select Arbitration Bodies..."
                    styles={selectStyles}
                    classNamePrefix="lreg-select"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="mt-3">
                <button
                  type="button"
                  className="btn w-100 corp-btn"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? "Submitting..." : "Register as Lawyer"}
                </button>
              </div>

              <div className="corp-form-note mt-2 small text-muted">
                By registering you agree to our Terms of Service and Privacy Policy. All documents are reviewed by our compliance team.
              </div>
            </div>
          </div>

          {/* ── Sidebar column ── */}
          <div className="col-lg-5" data-aos="fade-up">
            <div className="lreg-sidebar">
              <h4 className="lreg-sidebar-title">Why Join Legal Platform?</h4>

              <div className="lreg-benefit-card" data-aos="fade-left">
                <div className="lreg-icon-circle lreg-icon-blue">
                  <i className="bi bi-globe2" />
                </div>
                <div>
                  <h6 className="lreg-benefit-title">Global Reach</h6>
                  <p className="lreg-benefit-text">Connect with clients across multiple jurisdictions and expand your practice beyond borders.</p>
                </div>
              </div>

              <div className="lreg-benefit-card" data-aos="fade-left">
                <div className="lreg-icon-circle lreg-icon-green">
                  <i className="bi bi-shield-check" />
                </div>
                <div>
                  <h6 className="lreg-benefit-title">Verified Profile</h6>
                  <p className="lreg-benefit-text">Get a verified badge that builds client trust and sets you apart in the competitive legal market.</p>
                </div>
              </div>

              <div className="lreg-benefit-card" data-aos="fade-left">
                <div className="lreg-icon-circle lreg-icon-gold">
                  <i className="bi bi-lightning-charge" />
                </div>
                <div>
                  <h6 className="lreg-benefit-title">Instant Matching</h6>
                  <p className="lreg-benefit-text">Our smart matching system connects you with clients that need your specific expertise instantly.</p>
                </div>
              </div>

              <div className="lreg-benefit-card" data-aos="fade-left">
                <div className="lreg-icon-circle lreg-icon-purple">
                  <i className="bi bi-cash-coin" />
                </div>
                <div>
                  <h6 className="lreg-benefit-title">Transparent Earnings</h6>
                  <p className="lreg-benefit-text">Track consultations, payments, and client history all in one dashboard with full transparency.</p>
                </div>
              </div>

              <div className="lreg-commitment-box">
                <h6 className="lreg-commitment-title">Our Commitment</h6>
                <p className="lreg-commitment-text">We handle client acquisition and platform operations so you can focus on delivering excellent legal services. Join a growing network of verified lawyers trusted globally.</p>
              </div>
            </div>
          </div>

        </div>
      </main>

      {showTop && (
        <button
          type="button"
          className="scroll-top-btn"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          style={{ "--progress": `${Math.round(scrollProgress * 100)}%` }}
        >
          <span className="scroll-top-ring" />
          <i className="bi bi-arrow-up-short" />
        </button>
      )}

      <Footer />
    </div>
  );
};

export default LawyerRegistration;
